import AemInlineNotification from '@/components/Aem/AemInlineNotification';
import BadgeProgressBar from '@/components/BadgeCard/BadgeProgressBar';
import BadgeTag from '@/components/BadgeCard/BadgeTag';
import CloseButton from '@/components/Buttons/CloseButton';
import {ChevronLeftIcon} from '@/components/Icons';
import {INLINE_NOTIFICATION_VARIANTS} from '@/consts/inlineNotification';
import useBadgeDetailRoute from '@/hooks/useBadgeDetailRoute';
import type {ImageModel, InlineNotificationModel, PluralFormsModel} from '@/types/aem';
import type {Badge} from '@/types/loyalty';

import BadgeDetailHero from './BadgeDetailHero';

type BadgeDetailHeaderProps = {
    title?: string;
    titleId?: string;
    imageUrl?: string;
    backgroundImage?: ImageModel[];
    notification?: InlineNotificationModel | null;
    isEarned?: boolean;
    badge?: Badge;
    totalSteps?: number;
    finishedSteps?: number;
    remainingStepsPluralForms?: PluralFormsModel;
    onBack?: () => void;
};

const BadgeDetailHeader = ({
    title,
    titleId,
    imageUrl,
    backgroundImage,
    notification,
    isEarned,
    badge,
    totalSteps,
    finishedSteps,
    remainingStepsPluralForms,
    onBack,
}: BadgeDetailHeaderProps) => {
    const {closePopup} = useBadgeDetailRoute();
    const hasProgress = typeof totalSteps === 'number' && typeof finishedSteps === 'number';

    return (
        <div className='relative isolate w-full overflow-hidden'>
            <div className='relative aspect-393/579 w-full'>
                <BadgeDetailHero imageUrl={imageUrl} backgroundImage={backgroundImage} />

                <CloseButton className='absolute top-app-4 right-4 z-10' onClick={closePopup} />
                {onBack && (
                    <CloseButton
                        className='absolute top-app-4 left-4 z-10'
                        onClick={onBack}
                        icon={<ChevronLeftIcon />}
                    />
                )}
                {!onBack && badge && (
                    <div className='absolute top-app-4 left-4 z-10 flex h-11 items-center'>
                        <BadgeTag badge={badge} className='py-2 text-14 backdrop-blur-sm' />
                    </div>
                )}
            </div>

            <div className='relative -mt-[40%] flex flex-col items-center bg-linear-to-b from-transparent to-secondary px-4 pt-6 pb-0'>

                <div className='relative z-10 flex w-full flex-col items-center gap-4'>
                    {title && (
                        <h2 id={titleId} className='m-0 text-center font-bold text-28 leading-143'>
                            {title}
                        </h2>
                    )}

                    {(hasProgress || notification) && (
                        <div className='flex w-full flex-col gap-8'>
                            {hasProgress && (
                                <BadgeProgressBar
                                    totalSteps={totalSteps}
                                    finishedSteps={finishedSteps}
                                    actionsRemainingPluralForms={remainingStepsPluralForms}
                                    className='w-full'
                                />
                            )}
                            {notification && (
                                <AemInlineNotification
                                    model={notification}
                                    variant={
                                        isEarned
                                            ? INLINE_NOTIFICATION_VARIANTS.DEFAULT
                                            : INLINE_NOTIFICATION_VARIANTS.GRADIENT
                                    }
                                    className='bg-obsidian'
                                    gradientWrapperClassName='w-full'
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BadgeDetailHeader;
